import { AfterViewChecked, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  PointLeaderboardService,
  CategoryLevel,
  Category,
  Standing,
  User,
  SeasonsResponse,
  RankingsResponse,
  TimelineResponse,
  LeaderboardSystemSettings,
  LeaderboardMode,
  CLUBS_PAGE_SIZE,
} from '../../utilities/services/point-leaderboard/point-leaderboard.service';
import { map, of, Subject, switchMap } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { LeaderboardSidebarComponent } from './leaderboardsidebar/leaderboardsidebar.component';
import { PointBreakdownComponent } from './point-breakdown/point-breakdown.component';
import { TranslateModule } from '@ngx-translate/core';
import { RoleServiceService } from '../../utilities/services/role-service/role-service.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SkeletonLoaderComponent } from '../shared/skeleton-loader/skeleton-loader.component';
import { RequestTracker } from '../../utilities/services/loading/request-tracker';
import { CommonServiceService } from '../../utilities/services/common-service/common-service.service';
import { MobileParticipantCategorySelectorComponent } from '../shared/mobile-participant-category-selector/mobile-participant-category-selector.component';
import { TooltipModule } from 'primeng/tooltip';
import {
  headerAbbr,
  headerAbbrFull,
  legendItemsFromKeys,
  type HeaderAbbrKey,
} from '../../shared/header-abbr/header-abbr';
import {
  TableLegendOverlayComponent,
  type LegendItem,
} from '../shared/table-legend-overlay/table-legend-overlay.component';
import { environment } from '../../../environments/environment';
import { TemplateService } from '../../utilities/services/template/template.service';

export interface PlayerRanking {
  gender_id: number | null;
  rank: number;
  player: string;
  clubMa: string;
  points?: number;
  bonusPoints?: number;
  eloPoints?: number;
  totalPoints?: number;
  breakdown?: string;
  movementIndicator?: string;
  is_paused?: boolean;
  player_profile?: string | null;
  country_flag?: string;
  participant_id?: number;
  event_points?: { [eventId: number]: number };
  parents?: { name: string; country: string | null; role_id: number; role_name: string; country_flag: string | null; user_role_id: number }[];
  user_info?: {
    gender: string;
  };
}

interface BreakdownPointsSummary {
  eloPoints: number | null;
  bonusPoints: number | null;
  totalPoints: number | null;
}

interface CustomEvent {
  event_id: number;
  event_name: string;
}

interface ParentRoleEntry {
  parent_role_id: number;
  parent_role_name: string;
  parent_user_roles: Array<{ parent_role_linked_field_value: string | null }>;
}

@Component({
  selector: 'stupa-point-leaderboard',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    LeaderboardSidebarComponent,
    PointBreakdownComponent,
    SkeletonLoaderComponent,
    MobileParticipantCategorySelectorComponent,
    TooltipModule,
    TableLegendOverlayComponent,
  ],
  templateUrl: './point-leaderboard.component.html',
  styleUrl: './point-leaderboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:resize)': 'onResize()',
    '(window:scroll)': 'onWindowScroll()',
  },
})
export class PointLeaderboardComponent implements OnInit, AfterViewChecked, AfterViewInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly leaderboardService = inject(PointLeaderboardService);
  private readonly roleService = inject(RoleServiceService);
  private readonly templateService = inject(TemplateService);

  /** True when the OTT layout (template 1) is active — see {@link TemplateService}. */
  get isTemplate1(): boolean {
    return this.templateService.isTemplate1;
  }

  commonStorageUrl = environment.common_storage_url ?? 'https://storage.googleapis.com/stupaprodsguscentral/';
  podiumBackgroundNgStyle = {
    'background-image': `var(--ranking-leaderboard-podium-image, url(${this.commonStorageUrl}/ettu-website-wordpress/2025/04/3bccfb9e7a230e47eca5560d1e7aaad2.png))`,
  };

  // Data for levels and categories
  levels: CategoryLevel[] = [];
  selectedLevel: string = '';
  categories: Category[] = [];
  selectedCategory: string = '';
  isDropdownOpen: boolean = false;
  isCategoryDropdownOpen: boolean = false;
  selectedCategoryId: number | null = null;
  mobileParticipantOptions: { id: number; title: string }[] = [];
  category_list: {
    category_id: number;
    category_description: string;
    participant_type_id: number;
    id: number;
  }[] = [];
  category_list_copy: {
    category_id: number;
    category_description: string;
    participant_type_id: number;
    id: number;
  }[] = [];

  // Sidebar visibility
  isSidebarOpen: boolean = false;
  isBreakdownSidebarOpen: boolean = false;
  selectedParticipantId: number | null = null;
  selectedBreakdownPoints: BreakdownPointsSummary | null = null;

  // Ranking data
  rankings: PlayerRanking[] = [];
  /** Cached top 3 for podium (ranks 1,2,3) so podium stays correct when on page 2+. */
  podiumTop3: (PlayerRanking | undefined)[] = [];
  parents: number[] = [];
  filterRankMin?: number;
  filterRankMax?: number;
  dateFrom?: string;
  dateTo?: string;
  filterEventIds: number[] = [];
  filterSeasonId: string = ''; // Store the season ID from filters

  // Search term and debouncing
  searchTerm: string = '';
  private readonly searchSubject: Subject<string> = new Subject();

  // Pagination data
  currentPage: number = 1;
  itemsPerPage: number = 50;
  totalPages: number = 1;
  totalItems: number = 0;
  itemsPerPageOptions: number[] = [10, 20, 30, 40, 50, 100];

  // Mobile infinite scroll
  isMobileView: boolean = false;
  mobileItemsPerPage: number = 50;
  mobileLoadingMore: boolean = false;
  private readonly MOBILE_BREAKPOINT = 768;
  /** Matches events-results: desktop layout uses grid; <=1024 uses mobile table. */
  private layoutWide1024Cache =
    globalThis.window === undefined ? true : globalThis.window.innerWidth > 1024;

  get layoutWide1024(): boolean {
    return this.layoutWide1024Cache;
  }

  /** Cached template values — rebuilt only when layout dependencies change. */
  private viewLayoutCacheKey = '';
  private _gridTemplateColumns = '';
  private _filteredEventNamesForView: CustomEvent[] = [];
  private _plbMobileStatColIndices: number[] = [];
  private _pageNumbersCache: number[] = [];
  podiumRank1?: PlayerRanking;
  podiumRank2?: PlayerRanking;
  podiumRank3?: PlayerRanking;

  /** Stable track key for ranking rows in @for loops. */
  trackRanking(_index: number, ranking: PlayerRanking): number | string {
    return ranking.participant_id ?? `${ranking.rank}-${ranking.player}`;
  }

  get gridTemplateColumns(): string {
    this.refreshViewLayoutCacheIfStale();
    return this._gridTemplateColumns;
  }

  get filteredEventNamesForView(): CustomEvent[] {
    this.refreshViewLayoutCacheIfStale();
    return this._filteredEventNamesForView;
  }

  get plbMobileStatColIndices(): number[] {
    this.refreshViewLayoutCacheIfStale();
    return this._plbMobileStatColIndices;
  }

  get pageNumbersForView(): number[] {
    this.refreshViewLayoutCacheIfStale();
    return this._pageNumbersCache;
  }

  private refreshViewLayoutCacheIfStale(): void {
    const filteredNames =
      this.filterEventIds.length > 0 ? this.getFilteredEventNames() : [];
    const roleColCount = this.parent_role_list.length > 0 ? this.parent_role_list.length : 1;
    const cacheKey = [
      this.isMobileView,
      this.leaderboardMode,
      this.filterEventIds.join(','),
      this.events.map((e) => e.event_id).join(','),
      roleColCount,
      this.currentPage,
      this.totalPages,
      this.podiumTop3.map((p) => p?.participant_id ?? p?.rank ?? '').join(','),
    ].join('|');

    if (cacheKey === this.viewLayoutCacheKey) {
      return;
    }

    this.viewLayoutCacheKey = cacheKey;
    this._gridTemplateColumns = this.buildGridTemplateColumns(filteredNames);
    this._filteredEventNamesForView = filteredNames;

    let statCount: number;
    if (this.leaderboardMode === 'RankingAndRating') {
      statCount = roleColCount + 3;
    } else {
      statCount = roleColCount + 1 + filteredNames.length;
    }
    this._plbMobileStatColIndices = Array.from({ length: statCount }, (_, i) => i);
    this._pageNumbersCache = this.buildPageNumbers();
    this.podiumRank1 = this.podiumTop3[0];
    this.podiumRank2 = this.podiumTop3[1];
    this.podiumRank3 = this.podiumTop3[2];
  }

  /** Short mobile header label from the shared header-abbr registry. */
  setHeaderAbbr(key: HeaderAbbrKey): string {
    return headerAbbr(key);
  }

  setHeaderAbbrFull(key: HeaderAbbrKey): string | undefined {
    return headerAbbrFull(key);
  }

  /** All visible mobile abbreviations → full labels (same idea as events-results group table legend). */
  getLeaderboardMobileLegendItems(): LegendItem[] {
    return [
      ...this.buildLeaderboardRoleLegendItems(),
      ...this.buildLeaderboardStatLegendItems(),
    ];
  }

  private buildLeaderboardRoleLegendItems(): LegendItem[] {
    const roles = this.getDisplayableRoles();
    if (roles.length === 0) {
      return legendItemsFromKeys(['club']);
    }
    const items: LegendItem[] = [];
    for (const role of roles) {
      const abbr = this.mobileLeaderboardRoleHeader(role);
      const full = (role.parent_role_name || '').trim();
      if (abbr && full) {
        items.push({ abbr, full });
      }
    }
    return items;
  }

  private buildLeaderboardStatLegendItems(): LegendItem[] {
    if (this.leaderboardMode === 'RankingAndRating') {
      return legendItemsFromKeys([
        'bonus_points',
        'elo_points',
        'total_points',
      ]);
    }
    const items = legendItemsFromKeys(['points']);
    if (!this.filterEventIds?.length) {
      return items;
    }
    for (const ev of this.getFilteredEventNames()) {
      const abbr = this.mobileLeaderboardEventHeader(ev);
      const full = (ev.event_name || '').trim();
      if (abbr && full) {
        items.push({ abbr, full });
      }
    }
    return items;
  }

  openLeaderboardMobileLegend(event: Event): void {
    this.activeLegendItems = this.getLeaderboardMobileLegendItems();
    this.cdr.detectChanges();
    this.leaderboardLegendOverlay?.toggle(event);
  }

  /**
   * Mobile thead: tap outside sort icons sorts whole header when non-combined rank/player/club;
   * tap sort icon sorts when combined (or other icon-based columns); otherwise opens legend overlay.
   */
  onMobileLeaderboardHeaderClick(
    event: Event,
    sortField: string | null,
  ): void {
    const target = event.target as HTMLElement | null;
    const onSortIcon = !!target?.closest('.sort-icon');
    const nonCombined = this.leaderboardMode !== 'RankingAndRating';
    const wholeHeaderSorts =
      !!sortField &&
      nonCombined &&
      (sortField === 'rank' ||
        sortField === 'player' ||
        sortField === 'club/ma');

    if (sortField && (onSortIcon || wholeHeaderSorts)) {
      this.toggleSort(sortField);
      return;
    }
    this.openLeaderboardMobileLegend(event);
  }

  /**
   * Abbreviated label for dynamic parent-role columns; uses headerAbbr for Club / MA when the name matches.
   */
  mobileLeaderboardRoleHeader(role: { parent_role_name: string }): string {
    const raw = (role?.parent_role_name || '').trim();
    if (!raw) return '';
    const norm = raw.toLowerCase().replace(/\s+/g, ' ').trim();
    if (norm === 'club') {
      return headerAbbr('club');
    }
    if (norm === 'member association' || norm === 'ma') {
      return headerAbbr('member_association');
    }
    const upper = raw.toUpperCase();
    if (upper.length <= 5) {
      return upper;
    }
    return upper.slice(0, 4) + '.';
  }

  /** Compact uppercase label for filtered event columns (no fixed header-abbr key). */
  mobileLeaderboardEventHeader(event: { event_name: string }): string {
    const raw = (event?.event_name || '').trim();
    if (!raw) return '';
    const upper = raw.toUpperCase();
    if (upper.length <= 8) {
      return upper;
    }
    return upper.slice(0, 6) + '.';
  }

  // Sorting data
  sortBy: string = 'rank';
  sortOrder: 'asc' | 'desc' = 'asc';

  // Preloaded filter data
  clubs: User[] = [];
  clubsRoleId: number | null = null;
  seasons: SeasonsResponse['seasons'] = [];
  events: CustomEvent[] = [];
  timeline: TimelineResponse | null = null;
  selectedSeasonId: string = '';
  selectedTimelineId: string | null = null;
  timelineSeasonId: string = '';

  // Loader for dynamic standings API
  _showLoader: boolean = false;
  readonly setupLoading = new RequestTracker();

  get showStandingsSkeleton(): boolean {
    if (this.isCategoryOnHold) {
      return false;
    }
    if (this.rankings.length === 0) {
      return this.load_standings || this.setupLoading.loading();
    }
    return this._showLoader;
  }

  // Dynamic parent role columns (Club/MA, Member Association, etc.)
  parent_role_list: ParentRoleEntry[] = [];
  playerRoleId: number | null = null;

  // Reference to the category list container
  @ViewChild('categoryList', { static: false })
  categoryList!: ElementRef<HTMLDivElement>;

  // Reference to the sidebar component
  @ViewChild(LeaderboardSidebarComponent, { static: false })
  sidebarComponent!: LeaderboardSidebarComponent;

  @ViewChild('leaderboardLegendOverlay', { static: false })
  private readonly leaderboardLegendOverlay?: TableLegendOverlayComponent;

  /** Mobile table: items for the table legend overlay (abbr → full). */
  activeLegendItems: LegendItem[] = [];

  // Track if scrolling is needed
  private needsScroll: boolean = false;

  // Podium related properties - Always true for expanded view
  isExpanded: boolean = true;
  private routeParamsBound: boolean = false;
  private hasTriggeredInitialStandingsLoad: boolean = false;
  /** Full categories list — fetched once, filtered per level client-side. */
  private allCategoriesCache: Category[] | null = null;

  // Mobile search toggle
  mobileSearchExpanded: boolean = false;

  toggleMobileSearch(): void {
    this.mobileSearchExpanded = !this.mobileSearchExpanded;
  }

  get activeLevelId(): number | null {
    return this.levels.find((level) => level.level === this.selectedLevel)?.id ?? null;
  }

  rankingListLastUpdatedAt: string | null = null;
  current_participant_name: string | null = null;
  load_standings: boolean = false;
  leaderboardSystemSettings: LeaderboardSystemSettings | null = null;
  leaderboardMode: LeaderboardMode = 'None';
  /** True after the rankings API has completed at least once (success or error). Used to avoid showing "no records" before first load. */
  hasLoadedStandings: boolean = false;
  // Get event names for filtered event IDs
  getFilteredEventNames(): { event_id: number; event_name: string }[] {
    return this.filterEventIds
      .map((id) => this.events.find((event) => event.event_id === id))
      .filter((event): event is CustomEvent => !!event);
  }

  getRole(): void {
    this.roleService.getRoles()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const roles = (res as unknown as { data?: { roles: Array<{ id: number; name: string }> } })?.data?.roles;
          this.playerRoleId = roles?.find((c) => c.name === 'Player')?.id ?? null;
        },
        error: (err: unknown) => console.error('Error fetching roles:', err),
        complete: () => this.getRoleNames(),
      });
  }

  getRoleNames(): void {
    if (!this.playerRoleId) return;
    this.roleService.get_roles_entity(this.playerRoleId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const data = (res as { data?: ParentRoleEntry[] })?.data || [];
          this.parent_role_list = data
            .filter((x) => x.parent_role_id !== 1)
            .filter((x) => (x.parent_role_name || '').toLowerCase() !== 'admin')
            .map((role) => ({
              ...role,
              parent_user_roles: (role.parent_user_roles || []).filter(
                (item) =>
                  item.parent_role_linked_field_value !== '' &&
                  item.parent_role_linked_field_value != null,
              ),
            }));
          this.cdr.detectChanges();
        },
        error: (err: unknown) => console.error('Error fetching roles:', err),
      });
  }

  getDisplayableRoles(): ParentRoleEntry[] {
    return this.parent_role_list || [];
  }

  /** Get parent value for a given role from ranking's parents (top-level) */
  getParentValueForRole(
    ranking: PlayerRanking,
    parentRoleId: number,
  ): string {
    const parents = ranking?.parents || [];
    const match = parents.find(
      (p) =>
        Number(
          (p as { role_id?: number; parent_role_id?: number; user_role_id?: number })
            ?.role_id ??
            (p as { parent_role_id?: number })?.parent_role_id ??
            p?.user_role_id,
        ) === Number(parentRoleId),
    ) as
      | {
          name?: string;
          parent_role_field_value?: string;
          parent_role_linked_field_value?: string;
        }
      | undefined;
    return (
      match?.name ||
      match?.parent_role_field_value ||
      match?.parent_role_linked_field_value ||
      ''
    );
  }

  get isCategoryOnHold(): boolean {
    return this.categories.find(c => c.category_description === this.selectedCategory)?.is_on_hold ?? false;
  }

  get categoryHoldReason(): string | null {
    return this.categories.find(c => c.category_description === this.selectedCategory)?.hold_reason ?? null;
  }

  // Check if any filters are applied
  get areFiltersApplied(): boolean {
    return (
      this.parents.length > 0 ||
      this.filterRankMin !== undefined ||
      this.filterRankMax !== undefined ||
      this.dateFrom !== undefined ||
      this.dateTo !== undefined ||
      this.filterEventIds.length > 0 ||
      (this.filterSeasonId !== '' &&
        this.filterSeasonId !== this.selectedSeasonId)
    );
  }


  private readonly commonService = inject(CommonServiceService);

  ngOnInit(): void {
    this.checkMobileView();
    this.getRole();
    // Move fetchCategoryLevels into preloadFilterData to ensure seasonId is set
    this.preloadFilterData();
    this.systemSettings();
    // Set up search debouncing
    this.searchSubject
      .pipe(
        tap(() => {
          this._showLoader = true;
        }),
        debounceTime(500),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((searchTerm) => {
        this.searchTerm = searchTerm;
        const levelId = this.levels.find(
          (l) => l.level === this.selectedLevel,
        )?.id;
        const categoryId = this.categories.find(
          (c) => c.category_description === this.selectedCategory,
        )?.id;

        if (levelId && categoryId) {
          this.reloadCurrentStandings(true);
        }
      });
  }




  private formatDateForApi(date: Date): string {
    // Format date as YYYY-MM-DD
    return date.toISOString().split('T')[0];
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }

  onResize(): void {
    this.checkMobileView();
  }

  private checkMobileView(): void {
    if (globalThis.window !== undefined) {
      const wasMobile = this.isMobileView;
      const wasWide = this.layoutWide1024Cache;
      this.isMobileView = globalThis.window.innerWidth < this.MOBILE_BREAKPOINT;
      this.layoutWide1024Cache = globalThis.window.innerWidth > 1024;
      if (
        wasMobile !== this.isMobileView ||
        wasWide !== this.layoutWide1024Cache
      ) {
        this.cdr.detectChanges();
      }
    }
  }

  onWindowScroll(): void {
    if (!this.isMobileView || this.mobileLoadingMore || this.currentPage >= this.totalPages) return;
    if (this.rankings.length === 0 || this._showLoader) return;

    const scrollTop = globalThis.window.scrollY || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = globalThis.window.innerHeight;
    const threshold = 200;

    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      this.loadMoreForMobile();
    }
  }

  loadMoreForMobile(): void {
    if (!this.isMobileView || this.mobileLoadingMore || this.currentPage >= this.totalPages) return;

    const levelId = this.levels.find((l) => l.level === this.selectedLevel)?.id;
    const categoryId = this.categories.find(
      (c) => c.category_description === this.selectedCategory,
    )?.id;
    if (!levelId || !categoryId) return;

    this.mobileLoadingMore = true;
    this.currentPage++;
    this.fetchRankings(
      levelId,
      categoryId,
      this.parents,
      this.filterRankMin,
      this.filterRankMax,
      this.dateFrom,
      this.dateTo,
      this.searchTerm,
      this.filterEventIds,
      this.filterSeasonId,
      true, // append
    );
  }

  private getCurrentDate(): Date {
    return new Date();
  }

  private preloadFilterData(): void {
    this.getCurrentDate();

    // Fetch clubs — switchMap eliminates nested subscription and cancels in-flight requests
    this.leaderboardService.getAllRoles().pipe(
      map((res) => {
        const maRole = (res?.msg?.data?.roles as Array<{ id: number; name: string }> | undefined)
          ?.find((role) => role.name === 'Member Association');
        return maRole?.id ?? null;
      }),
      switchMap((maRoleId) => {
        this.clubsRoleId = maRoleId;
        if (maRoleId == null) {
          return of({ users: [], count: 0 });
        }
        return this.leaderboardService.getUsers({
          roleId: maRoleId,
          pageNum: 1,
          perPage: CLUBS_PAGE_SIZE,
        });
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({ users }) => {
      this.clubs = users;
      this.cdr.detectChanges();
    });

    // Seasons are not used in point leaderboard flow anymore.
    // Keep season-related state empty and continue with current data-loading logic.
    this.seasons = [];
    this.selectedSeasonId = '';
    this.timelineSeasonId = '';
    this.fetchCategoryLevels();
  }

  private fetchTimeline(categoryId: number): void {
    if (!this.selectedSeasonId) return;
    const seasonIdToFetch = this.filterSeasonId || this.selectedSeasonId;
    this.leaderboardService
      .getRankingTimeline(Number(seasonIdToFetch), categoryId)
      .pipe(this.setupLoading.track(), takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        this.timeline = response;
        this.timelineSeasonId = seasonIdToFetch;
        const timelineArray = this.getTimelineArray(response);
        if (timelineArray) {
          // Find the maximum (most recent) date string.
          // String comparison works for 'YYYY-MM-DD' format.
          const dates = timelineArray.map((item) => item.date);
          const mostRecentDate = dates.length
            ? [...dates].sort((a, b) => a.localeCompare(b)).at(-1) ?? ''
            : '';
          this.rankingListLastUpdatedAt = mostRecentDate;
        } else {
          // 4. If no entries are completed, there is no "last updated" date.
          this.rankingListLastUpdatedAt = null;
        }
        const latestCompleted = timelineArray.find(
          (item) => item.status === 'completed',
        );
        this.selectedTimelineId = latestCompleted
          ? latestCompleted.ranking_list_id
          : null;
        this.cdr.detectChanges();
      });
  }

  private updateEvents(response: RankingsResponse, searchValue: string): void {
    const today = this.getCurrentDate();
    const eventMap: {
      [key: number]: {
        event_id: number;
        event_name: string;
        hasActivePoints: boolean;
      };
    } = {};

    response.standings.forEach((standing) => {
      standing.event_breakdown.forEach((event) => {
        const expiryDate = new Date(event.expiry_date);
        const isActive = expiryDate >= today;
        if (!eventMap[event.event_id]) {
          eventMap[event.event_id] = {
            event_id: event.event_id,
            event_name: event.event_name,
            hasActivePoints: isActive,
          };
        } else if (isActive) {
          eventMap[event.event_id].hasActivePoints = true;
        }
      });
    });

    let events = Object.values(eventMap)
      .filter((event) => event.hasActivePoints)
      .map((event) => ({
        event_id: event.event_id,
        event_name: event.event_name,
      }));

    if (searchValue) {
      events = events.filter((event) =>
        event.event_name.toLowerCase().includes(searchValue.toLowerCase()),
      );
    }

    this.events = events;
  }

  private getTimelineArray(response: TimelineResponse): any[] {
    switch (response.configure) {
      case 'at_the_end_of_every_event':
      case 'day':
        return response.by_date;
      case 'month':
        return response.by_month;
      case 'week':
        return response.by_week;
      default:
        return [];
    }
  }

  ngAfterViewChecked(): void {
    if (this.needsScroll && this.categoryList) {
      this.scrollToActiveCategory();
      this.needsScroll = false;
    }
  }

  private fetchCategoryLevels(): void {
    this.leaderboardService
      .getCategoryLevels()
      .pipe(this.setupLoading.track(),
        map((levels: CategoryLevel[]) =>
          levels.map((level) => ({
            ...level,
            level: this.capitalize(level.level),
          })),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (levels: CategoryLevel[]) => {
          this.levels = levels;
          this.mobileParticipantOptions = this.levels.map((level) => ({
            id: level.id,
            title: level.level,
          }));
          if (this.routeParamsBound) {
            this.handleRouteParams(this.activatedRoute.snapshot.params);
          } else {
            this.routeParamsBound = true;
            this.activatedRoute.params
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe((params) => this.handleRouteParams(params));
          }
        },
        error: (error) => {
          console.error('Failed to fetch levels:', error);
          this.levels = [];
          this.selectedLevel = '';
          this.categories = [];
          this.rankings = [];
          this.cdr.detectChanges();
        },
      });
  }

  private handleRouteParams(params: Params): void {
    const levelId = params['levelId'];
    const categoryId = params['categoryId'];

    if (levelId) {
      const selected = this.levels.find((level) => level.id === +levelId);
      this.selectedLevel = selected ? selected.level : this.levels[0]?.level || '';
      this.fetchCategories(+levelId, categoryId);
    } else {
      this.selectedLevel = this.levels[0]?.level || '';
      if (this.levels[0]?.id) {
        // Avoid an intermediate self-navigation (/ranking -> /ranking/:levelId)
        // because it re-creates this component and duplicates initial API calls.
        this.fetchCategories(this.levels[0].id, categoryId);
      }
    }
    this.cdr.detectChanges();
  }

  private fetchCategories(levelId: number, categoryId?: string): void {
    if (this.allCategoriesCache !== null) {
      this.applyCategoriesForLevel(levelId, categoryId, this.allCategoriesCache);
      return;
    }

    this.leaderboardService.getCategories(true)
      .pipe(this.setupLoading.track(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (categories: Category[]) => {
          this.allCategoriesCache = categories;
          this.applyCategoriesForLevel(levelId, categoryId, categories);
        },
        error: (error) => {
          console.error('Failed to fetch categories:', error);
          this.categories = [];
          this.selectedCategory = '';
          this.selectedCategoryId = null;
          this.category_list = [];
          this.category_list_copy = [];
          this.rankings = [];
          this.totalPages = 1;
          this.totalItems = 0;
          this.currentPage = 1;
          this._showLoader = false;
          this.cdr.detectChanges();
        },
        complete: () => {
          this._showLoader = false;
          this.cdr.detectChanges();
        },
      });
  }

  private getCategoriesForLevel(levelId: number, allCategories: Category[]): Category[] {
    return allCategories.filter(
      (category) =>
        category.category_level_id === levelId &&
        category.participant_type_id === 1,
    );
  }

  /** Maps and filters categories for mobile selector (same pattern as events-participants). */
  private syncCategoryListForMobile(levelId: number | null): void {
    const allCategories = this.allCategoriesCache ?? [];
    this.category_list_copy = allCategories
      .filter(
        (category) =>
          category.participant_type_id === 1 &&
          category.category_level_id != null,
      )
      .map((category) => ({
        category_id: category.id,
        category_description: category.category_description,
        participant_type_id: category.category_level_id as number,
        id: category.id,
      }));

    if (levelId == null) {
      this.category_list = [];
    } else {
      this.category_list = this.category_list_copy.filter(
        (x) => x.participant_type_id == levelId,
      );
    }
  }

  private getFirstCategoryIdForLevel(levelId: number): number | undefined {
    return this.getCategoriesForLevel(levelId, this.allCategoriesCache ?? [])[0]?.id;
  }

  private applyCategoriesForLevel(
    levelId: number,
    categoryId: string | undefined,
    allCategories: Category[],
  ): void {
    this.categories = this.getCategoriesForLevel(levelId, allCategories);
    this.syncCategoryListForMobile(levelId);

    if (categoryId) {
      const selected = this.categories.find(
        (category) => category.id === +categoryId,
      );
      this.selectedCategory = selected
        ? selected.category_description
        : this.categories[0]?.category_description || '';
      this.selectedCategoryId = selected
        ? selected.id
        : this.categories[0]?.id || null;
    } else {
      this.selectedCategory =
        this.categories[0]?.category_description || '';
      this.selectedCategoryId = this.categories[0]?.id || null;
      if (this.categories[0]?.id) {
        this.router.navigate(['/ranking', levelId, this.categories[0].id]);
        this.needsScroll = true;
        this.updateScrollArrows();
        this.cdr.detectChanges();
        return;
      }
    }
    this.needsScroll = true;
    this.updateScrollArrows();
    if (categoryId || this.categories[0]?.id) {
      this.currentPage = 1;
      this.podiumTop3 = [];
      const selectedCategoryId = categoryId
        ? +categoryId
        : this.categories[0].id;
      this.loadStandingsByMode(levelId, selectedCategoryId);
    }
    this.cdr.detectChanges();
  }

  private loadStandingsByMode(levelId: number, categoryId: number): void {
    this.hasTriggeredInitialStandingsLoad = true;
    // Read latest mode from service in case system settings finished after initial category load.
    this.leaderboardMode = this.leaderboardService.getCurrentLeaderboardMode();

    if (this.leaderboardMode === 'Ranking') {
      this.fetchRankings(
        levelId,
        categoryId,
        this.parents,
        this.filterRankMin,
        this.filterRankMax,
        this.dateFrom,
        this.dateTo,
        this.searchTerm,
        this.filterEventIds,
        this.filterSeasonId,
      );
      return;
    }

    if (this.leaderboardMode === 'RankingAndRating') {
      this.fetchCombinedStandings();
    }
  }

  private getSelectedLevelAndCategoryIds(): {
    levelId?: number;
    categoryId?: number;
  } {
    const levelId = this.levels.find((l) => l.level === this.selectedLevel)?.id;
    const categoryId = this.categories.find(
      (c) => c.category_description === this.selectedCategory,
    )?.id;
    return { levelId, categoryId };
  }

  private reloadCurrentStandings(resetPage: boolean = false): void {
    const { levelId, categoryId } = this.getSelectedLevelAndCategoryIds();
    if (!levelId || !categoryId) {
      return;
    }
    if (resetPage) {
      this.currentPage = 1;
    }
    this.loadStandingsByMode(levelId, categoryId);
  }

  private fetchRankings(
    levelId: number,
    categoryId: number,
    parents: number[],
    filterRankMin?: number,
    filterRankMax?: number,
    dateFrom?: string,
    dateTo?: string,
    search?: string,
    filterEventIds: number[] = [],
    filterSeasonId?: string,
    append: boolean = false,
  ): void {
    // Determine season ID: use provided filterSeasonId if available, otherwise null (no season ID)
    // Don't use fallback to selectedSeasonId for initial calls
    const seasonId: number | null =
      filterSeasonId !== undefined && filterSeasonId
        ? Number(filterSeasonId)
        : null;

    const perPage = this.isMobileView ? this.mobileItemsPerPage : Number(this.itemsPerPage);
    const requestedPage = this.currentPage;

    if (!append) {
      this._showLoader = true;
      this.load_standings = true;
      this.rankings = [];
    }
    this.leaderboardService
      .getRankings(
        seasonId,
        categoryId,
        requestedPage,
        perPage,
        parents,
        filterRankMin,
        filterRankMax,
        dateFrom,
        dateTo,
        search,
        this.sortBy,
        this.sortOrder,
        filterEventIds,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: RankingsResponse) => {

          this._showLoader = false;
          this.load_standings = false;
          this.mobileLoadingMore = false;

          const mapped = response.items[0]?.data?.standings.length > 0
            ? response.items[0]?.data?.standings.map((standing: Standing) => {
              const eventPoints: { [eventId: number]: number } = {};
              standing?.event_breakdown.forEach((event) => {
                eventPoints[event.event_id] = event.total_points_from_event;
              });
              const parents = standing?.parents || [];
              const displayParent = parents.find((p: any) => p.role_id !== 1) || parents[0];
              return {
                rank: standing?.rank,
                player: standing?.participant_name,
                user_info: standing?.user_info,
                parents,
                clubMa: displayParent?.name || '',
                points: standing?.total_points,
                breakdown: '',
                movementIndicator: standing?.movement_indicator,
                is_paused: standing?.is_paused,
                player_profile: standing?.user_info?.player_profile,
                country_flag: displayParent?.country_flag || '',
                participant_id: standing?.participant_id,
                event_points: eventPoints,
                gender_id: standing?.gender_id
              };
            })
            : [];

          if (append) {
            this.rankings = [...this.rankings, ...mapped];
          } else {
            this.rankings = mapped;
          }

          const meta = response.items[0]?.data?.metadata;
          this.totalPages = meta?.total_pages || 1;
          this.totalItems = meta?.total_count || 0;
          // Keep UI in sync with the page we actually requested. Backend metadata
          // sometimes omits current_page (!== → || 1) or echoes a stale value.
          this.currentPage = Math.min(
            Math.max(requestedPage, 1),
            this.totalPages,
          );
          if (!this.isMobileView) {
            this.itemsPerPage = meta?.per_page || this.itemsPerPage;
          }

          if (this.currentPage === 1) {
            this.setPodiumTop3FromRankings();
          }

          // Update events from the same response to avoid duplicate API call
          const data = response?.items[0]?.data;
          if (data) {
            this.updateEvents(data, search || this.searchTerm);
          }

          
          
          this.hasLoadedStandings = true;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Failed to fetch rankings:', error);
          this._showLoader = false;
          this.load_standings = false;
          this.mobileLoadingMore = false;
          if (append) {
            this.currentPage--;
          } else {
            this.hasLoadedStandings = true;
            this.rankings = [];
            this.podiumTop3 = [];
            this.totalPages = 1;
            this.totalItems = 0;
            this.currentPage = 1;
          }
          this.cdr.detectChanges();
        },
        complete: () => {
          this.load_standings = false;
          this._showLoader = false;
          this.cdr.detectChanges();
        },
      });
  }

  onExportClick(): void {
    if (!this.selectedCategoryId) {
      console.warn('No category ID selected, cannot export rankings.');
      return;
    }
    const seasonId = this.filterSeasonId;
    if (!seasonId) {
      console.warn('No season ID available, cannot export rankings.');
      return;
    }
    const payload = {
      season_id: Number(seasonId),
      category_id: this.selectedCategoryId,
      date_from: this.dateFrom,
      date_to: this.dateTo,
      event_ids: this.filterEventIds.length > 0 ? this.filterEventIds : [],
      parents: this.parents.length > 0 ? this.parents : [],
      filter_rank_min: this.filterRankMin,
      filter_rank_max: this.filterRankMax,
      search: this.searchTerm || '',
      format: 'csv',
    };
    
    this._showLoader = true;
    this.leaderboardService.exportRankings(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (response: Blob) => {
        this._showLoader = false;
        const blob = new Blob([response], { type: 'text/csv' });
        const url = globalThis.window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rankings_${seasonId}_${this.selectedCategoryId}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        globalThis.window.URL.revokeObjectURL(url);
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error exporting rankings:', error);
        this._showLoader = false;
        this.cdr.detectChanges();
      },
      complete: () => {
        this._showLoader = false;
        this.cdr.detectChanges();
      },
    });
  }

  toggleSort(column: string): void {
    if (
      this.leaderboardMode === 'RankingAndRating' &&
      (column === 'club/ma' || column === 'player' || column === 'name')
    ) {
      return;
    }
    if (this.sortBy === column) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = column;
      this.sortOrder = 'asc';
    }
    this.reloadCurrentStandings(true);
  }

  isCombinedSortActive(column: string): boolean {
    return (
      this.leaderboardMode === 'RankingAndRating' && this.sortBy === column
    );
  }

  isCombinedSortDesc(column: string): boolean {
    return this.isCombinedSortActive(column) && this.sortOrder === 'desc';
  }

  onSearch(): void {
    this.searchSubject.next(this.searchTerm);
  }

  onApplyFilters(event: {
    parents: number[];
    filterRankMin?: number;
    filterRankMax?: number;
    dateFrom?: string;
    dateTo?: string;
    filterEventIds?: number[];
    filterSeasonId?: string;
  }) {
    this.parents = event.parents;
    this.filterRankMin = event.filterRankMin;
    this.filterRankMax = event.filterRankMax;
    this.dateFrom = event.dateFrom;
    this.dateTo = event.dateTo;
    this.filterEventIds = event.filterEventIds || [];
    this.filterSeasonId = event.filterSeasonId ?? '';
    const { categoryId } = this.getSelectedLevelAndCategoryIds();
    if (categoryId) {
      this.reloadCurrentStandings(true);
    }
    this.isSidebarOpen = false;
    this.cdr.detectChanges();
  }

  toggleSidebar(): void {
    this.isSidebarOpen = !this.isSidebarOpen;
    this.cdr.detectChanges();
  }

  closeSidebar(): void {
    this.isSidebarOpen = false;
    this.cdr.detectChanges();
  }

  onBreakdownClick(ranking: PlayerRanking, event?: Event): void {
    event?.stopPropagation();
    this.current_participant_name = ranking.player;

    if (ranking.participant_id) {
      this.selectedParticipantId = ranking.participant_id;
      this.selectedBreakdownPoints = {
        eloPoints: ranking.eloPoints ?? null,
        bonusPoints: ranking.bonusPoints ?? null,
        totalPoints: ranking.points ?? null,
      };
      this.isBreakdownSidebarOpen = true;
      this.cdr.detectChanges();
    }
  }

  closeBreakdownSidebar(): void {
    this.isBreakdownSidebarOpen = false;
    this.selectedParticipantId = null;
    this.selectedBreakdownPoints = null;
    this.cdr.detectChanges();
  }

  onPauseStatusChanged(): void {
    this.reloadCurrentStandings();
    this.closeBreakdownSidebar();
    this.cdr.detectChanges();
  }

  private capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  toggleLevelDropdown(event: Event) {
    event.stopPropagation();
    this.isCategoryDropdownOpen = false;
    this.isDropdownOpen = !this.isDropdownOpen;
    this.cdr.detectChanges();
  }

  toggleCategoryDropdown(event: Event) {
    event.stopPropagation();
    this.isDropdownOpen = false;
    this.isCategoryDropdownOpen = !this.isCategoryDropdownOpen;
    this.cdr.detectChanges();
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.isDropdownOpen = !this.isDropdownOpen;
    this.cdr.detectChanges();
  }

  selectLevel(level: string) {
    if (this.selectedLevel === level) {
      this.isDropdownOpen = false;
      return;
    }
    this.selectedLevel = level;
    this._showLoader = true;
    const selectedLevelObj = this.levels.find((l) => l.level === level);

    if (selectedLevelObj?.id) {
      const levelId = selectedLevelObj.id;
      const firstCategoryId = this.getFirstCategoryIdForLevel(levelId);
      if (firstCategoryId) {
        this.router.navigate(['/ranking', levelId, firstCategoryId]);
      } else {
        this.router.navigate(['/ranking', levelId]);
      }
    }
    this.isDropdownOpen = false;
    this.cdr.detectChanges();
  }

  selectCategory(category: string) {
    if (this.selectedCategory === category) {
      this.isCategoryDropdownOpen = false;
      return;
    }
    this.selectedCategory = category;
    this._showLoader = true;
    const selectedCategoryObj = this.categories.find(
      (c) => c.category_description === category,
    );
    
    const levelId = this.levels.find((l) => l.level === this.selectedLevel)?.id;
    if (selectedCategoryObj?.id && levelId) {
      this.selectedCategoryId = selectedCategoryObj.id;
      
      this.router.navigate(['/ranking', levelId, selectedCategoryObj.id]);
      this.needsScroll = true;
    } else {
      
      this.selectedCategoryId = null;
    }
    this.isCategoryDropdownOpen = false;
    this.updateScrollArrows();
    this.cdr.detectChanges();
  }

  onMobileLevelChange(levelId: number): void {
    const levelIdNum = Number(levelId);
    this.category_list = this.category_list_copy.filter(
      (x) => x.participant_type_id == levelIdNum,
    );
    const selectedLevelObj = this.levels.find((level) => level.id === levelIdNum);
    if (!selectedLevelObj) return;
    this.selectLevel(selectedLevelObj.level);
  }

  onMobileCategoryChange(categoryId: number): void {
    const selectedCategory =
      this.category_list.find((c) => c.category_id === Number(categoryId)) ??
      this.category_list_copy.find((c) => c.category_id === Number(categoryId));
    if (!selectedCategory) return;
    this.selectCategory(selectedCategory.category_description);
  }

  onContainerClick(event: Event) {
    const target = event.target as HTMLElement;
    const isLevelClick = target.closest('.level') || target.closest('.level-pill') || target.closest('.mobile-dropdown-menu');
    const isCategoryClick = target.closest('.category-pill') || target.closest('.mobile-dropdown-menu');
    const isMobileSearchClick = target.closest('.mobile-search-expanded') || target.closest('.mobile-filter-icon-btn');
    const isSidebarClick = target.closest('stupa-leaderboard-sidebar');
    const isBreakdownSidebarClick = target.closest('stupa-point-breakdown');
    const isExpiredPointsSidebarClick = target.closest('stupa-expired-points');
    const isEditSidebarClick = target.closest('stupa-edit-sidebar');
    const isVictoryPointOverlayClick = target.closest('.victory-point-overlay');
    const isFilterButtonClick = target.closest('.filters-btn');
    const isBreakdownButtonClick = target.closest('.breakdown-btn');
    const isSearchBarClick = target.closest('.search-bar');
    const isMobileBreakdownRowClick = target.closest(
      'tr.point-leaderboard-table-mobile__row',
    );

    if (!isSearchBarClick && !isMobileSearchClick && this.mobileSearchExpanded) {
      this.mobileSearchExpanded = false;
    }

    if (!isLevelClick && this.isDropdownOpen) {
      this.isDropdownOpen = false;
      this.cdr.detectChanges();
    }
    if (!isCategoryClick && this.isCategoryDropdownOpen) {
      this.isCategoryDropdownOpen = false;
      this.cdr.detectChanges();
    }
    if (
      !isSidebarClick &&
      !isFilterButtonClick &&
      this.isSidebarOpen &&
      !isBreakdownSidebarClick &&
      !isExpiredPointsSidebarClick &&
      !isEditSidebarClick &&
      !isVictoryPointOverlayClick
    ) {
      this.isSidebarOpen = false;
      this.cdr.detectChanges();
    }
    if (
      !isBreakdownSidebarClick &&
      !isExpiredPointsSidebarClick &&
      !isBreakdownButtonClick &&
      !isMobileBreakdownRowClick &&
      this.isBreakdownSidebarOpen &&
      !isEditSidebarClick &&
      !isVictoryPointOverlayClick
    ) {
      this.closeBreakdownSidebar();
    }
  }

  scrollCategories(direction: 'left' | 'right') {
    const categoryList = this.categoryList?.nativeElement;
    if (categoryList) {
      const scrollAmount = 200;
      categoryList.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
      setTimeout(() => this.updateScrollArrows(), 300);
    }
  }

  private scrollToActiveCategory(): void {
    if (!this.categoryList || !this.selectedCategory) {
      console.warn(
        'scrollToActiveCategory: categoryList or selectedCategory is undefined',
      );
      return;
    }

    const container = this.categoryList.nativeElement;
    const categoryElements = container.querySelectorAll(
      '.category-item',
    ) as NodeListOf<HTMLElement>;
    

    const index = this.categories.findIndex(
      (c) => c.category_description === this.selectedCategory,
    );
    

    if (index >= 0 && categoryElements[index]) {
      const element = categoryElements[index] as HTMLElement;
      const currentScrollLeft = container.scrollLeft;
      const visibleStart = currentScrollLeft;
      const visibleEnd = currentScrollLeft + container.clientWidth;
      const elementStart = element.offsetLeft;
      const elementEnd = elementStart + element.offsetWidth;
      const edgePadding = 16;

      let nextScrollLeft = currentScrollLeft;
      if (elementStart < visibleStart + edgePadding) {
        nextScrollLeft = Math.max(elementStart - edgePadding, 0);
      } else if (elementEnd > visibleEnd - edgePadding) {
        nextScrollLeft = elementEnd - container.clientWidth + edgePadding;
      }

      if (nextScrollLeft !== currentScrollLeft) {
        container.scrollTo({ left: nextScrollLeft, behavior: 'smooth' });
        
      }

      setTimeout(() => {
        this.updateScrollArrows();
        
      }, 300);
    } else {
      console.warn(
        'scrollToActiveCategory: Category element not found at index:',
        index,
        'Total elements:',
        categoryElements.length,
      );
    }
  }

  private updateScrollArrows(): void {
    const container = this.categoryList?.nativeElement;
    if (!container) return;

    const leftArrow = document.querySelector(
      '.scroll-btn.left',
    ) as HTMLButtonElement;
    const rightArrow = document.querySelector(
      '.scroll-btn.right',
    ) as HTMLButtonElement;
    const maxScroll = container.scrollWidth - container.clientWidth;

    if (leftArrow && rightArrow) {
      leftArrow.disabled = container.scrollLeft <= 0;
      rightArrow.disabled = container.scrollLeft >= maxScroll - 1;
      leftArrow.style.opacity = leftArrow.disabled ? '0.5' : '1';
      rightArrow.style.opacity = rightArrow.disabled ? '0.5' : '1';
      
    }
  }

  getStartIndex(): number {
    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  getEndIndex(): number {
    const end = this.currentPage * this.itemsPerPage;
    return Math.min(end, this.totalItems);
  }

  onItemsPerPageChange(): void {
    this.itemsPerPage = Number(this.itemsPerPage);
    this.reloadCurrentStandings(true);
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.reloadCurrentStandings();
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.reloadCurrentStandings();
    }
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.reloadCurrentStandings();
    }
  }

  goToFirstPage(): void {
    if (this.currentPage !== 1) {
      this.currentPage = 1;
      this.reloadCurrentStandings();
    }
  }

  goToLastPage(): void {
    if (this.currentPage !== this.totalPages) {
      this.currentPage = this.totalPages;
      this.reloadCurrentStandings();
    }
  }

  getPageNumbers(): number[] {
    this.refreshViewLayoutCacheIfStale();
    return this._pageNumbersCache;
  }

  private buildPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 7;
    const half = Math.floor(maxPagesToShow / 2);
    let startPage = Math.max(1, this.currentPage - half);
    let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  // --- Podium-related methods ---

  // Removed toggleExpand() as it's not needed for always expanded view

  /**
   * Returns a suffix for a given rank (e.g., "st", "nd", "rd", "th").
   * @param rank The rank number.
   * @returns The appropriate suffix.
   */
  getRankSuffix(rank: number): string {
    if (rank === 1) return 'st';
    if (rank === 2) return 'nd';
    if (rank === 3) return 'rd';
    return 'th';
  }

  /**
   * Returns a color code based on the rank (gold for 1st, silver for 2nd, green for 3rd).
   * @param rank The rank number.
   * @returns The hex color code.
   */
  getRankColor(rank: number): string {
    if (rank === 1) return '#E1CE60'; // Gold-like
    if (rank === 2) return '#E18560'; // Silver-like (using a different shade)
    if (rank === 3) return '#7DAD3A'; // Green for bronze
    return '#000000'; // Default color for other ranks (black)
  }

  /**
   * Returns the top three players from the `rankings` array.
   * This is used for the condensed podium display.
   * @returns An array containing the top three players.
   */
  getTopThreePlayers(): PlayerRanking[] {
    // Ensure rankings are sorted by rank before slicing
    const sortedRankings = [...this.rankings].sort((a, b) => a.rank - b.rank);
    return sortedRankings.slice(0, 3);
  }

  /**
   * Returns a specific player by their rank. Used for individual podium spots.
   * @param rank The rank of the player to retrieve.
   * @returns The PlayerRanking object or undefined if not found.
   */
  getPlayerByRank(rank: number): PlayerRanking | undefined {
    return this.rankings.find((player) => player.rank === rank);
  }

  /** Podium uses cached top 3 so names stay correct when viewing page 2+. */
  getPodiumPlayerByRank(rank: number): PlayerRanking | undefined {
    return this.podiumTop3[rank - 1];
  }

  private setPodiumTop3FromRankings(): void {
    const [first, second, third] = [1, 2, 3].map((rank) =>
      this.rankings.find((p) => p.rank === rank),
    );
    this.podiumTop3 = [first, second, third];
  }

  /**
   * Returns an array of players who share a specific rank. Used for potential ties (e.g., 3rd place).
   * @param rank The rank to filter players by.
   * @returns An array of PlayerRanking objects.
   */
  getPlayersByRank(rank: number): PlayerRanking[] {
    return this.rankings.filter((player) => player.rank === rank);
  }

  /**
   * Determines whether the podium should be displayed.
   * It checks if there are any rankings available with valid ranks.
   * @returns True if the podium should be shown, false otherwise.
   */
  showPodium(): boolean {
    return (
      this.rankings &&
      this.rankings.some((player) => player.rank !== null) &&
      !this.areFiltersApplied &&
      this.searchTerm.trim().length == 0 &&
      !this._showLoader
    );
  }

  /** @deprecated Use `gridTemplateColumns` getter in templates. Kept for backward compatibility. */
  getGridTemplateColumns(): string {
    return this._gridTemplateColumns;
  }

  private buildGridTemplateColumns(filteredEventNames: CustomEvent[]): string {
    const rankMinWidth = this.isMobileView ? 92 : 120;
    const columns = [
      `minmax(${rankMinWidth}px, 0.7fr)`,
      'minmax(130px, 3fr)',
    ];

    const roles = this.parent_role_list;
    if (roles.length > 0) {
      roles.forEach(() => columns.push('minmax(150px, 2fr)'));
    } else {
      columns.push('minmax(150px, 2fr)');
    }

    if (this.leaderboardMode === 'RankingAndRating') {
      columns.push(
        'minmax(120px, 1fr)',
        'minmax(120px, 1fr)',
        'minmax(120px, 1fr)',
        'minmax(100px, 0.5fr)',
      );
      return columns.join(' ');
    }

    columns.push('minmax(80px, 1fr)');

    filteredEventNames.forEach(() => {
      columns.push('minmax(120px, 1fr)');
    });

    columns.push('minmax(100px, 0.5fr)');
    return columns.join(' ');
  }
  removeFilters() {
    const filters = {
      parents: [],
      filterRankMin: undefined,
      filterRankMax: undefined,
      dateFrom: undefined,
      dateTo: undefined,
      filterEventIds: [],
      filterSeasonId: '', // Reset to default season
    };
    this.parents = filters.parents;
    this.filterRankMin = filters.filterRankMin;
    this.filterRankMax = filters.filterRankMax;
    this.dateFrom = filters.dateFrom;
    this.dateTo = filters.dateTo;
    this.filterEventIds = filters.filterEventIds || [];
    this.filterSeasonId = filters.filterSeasonId;
    const { categoryId } = this.getSelectedLevelAndCategoryIds();
    if (categoryId) {
      this.reloadCurrentStandings(true);
    }
  }

  setAvatarByGender(gender_id: number | string | null | undefined): string {
    if (gender_id == 1) {
      return '../../../assets/icons_svg/Default Player Male.svg';
    }
    return '../../../assets/icons_svg/Default Player Women.svg';
  }
  fetchCombinedStandings(): void {

    if (!this.selectedCategoryId) {
      console.warn(
        'Skipping combined standings: selectedCategoryId is not set yet.',
      );
      return;
    }
    const sortType = this.getCombinedSortType();
    const requestedPage = this.currentPage;
    const payload = {
      category_ids: this.selectedCategoryId,
      sort_by: sortType,
      sort_order: this.sortOrder || 'asc',
      page: requestedPage,
      per_page: this.itemsPerPage,
      search: this.searchTerm || '',
      filter_club_id: this.parents.length > 0 ? this.parents : [],
      filter_rank_min: this.filterRankMin,
      filter_rank_max: this.filterRankMax,
      date_from: this.dateFrom,
      date_to: this.dateTo,
      event_ids: this.filterEventIds.length > 0 ? this.filterEventIds : [],
      season_id: this.filterSeasonId ? Number(this.filterSeasonId) : undefined,
    };

    this._showLoader = true;
    this.load_standings = true;
    this.rankings = [];

    this.commonService.getCombinedStandings(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const items0 =
            response?.data?.items?.[0] || response?.data?.data?.items?.[0];
          const rawData =
            items0?.entries ||
            items0?.data ||
            response?.data?.entries ||
            response?.data?.data?.entries ||
            response?.data?.data ||
            response?.data;

          let standings: unknown[] = [];
          if (Array.isArray(rawData)) {
            standings = rawData;
          } else if (Array.isArray(rawData?.entries)) {
            standings = rawData.entries;
          } else if (Array.isArray(rawData?.standings)) {
            standings = rawData.standings;
          }
          this.rankings =
            standings.length > 0
              ? standings.map((standing: any) => {
                const rowParents =
                  standing?.parents ||
                  standing?.user_info?.parents ||
                  rawData?.parents ||
                  [];
                const displayParent =
                  rowParents.find(
                    (p: any) =>
                      Number(p?.role_id ?? p?.parent_role_id) !== 1,
                  ) || rowParents[0];
                return {
                  rank: standing?.rank,
                  player:
                    standing?.participant_name ||
                    standing?.player_name ||
                    standing?.name ||
                    '',
                  user_info: standing?.user_info || { gender: '' },
                  clubMa:
                    displayParent?.name ||
                    displayParent?.parent_role_field_value ||
                    standing?.user_info?.parents?.[0]?.parent_role_field_value ||
                    standing?.club_name ||
                    '',
                  bonusPoints:
                    standing?.ranking_bonus_points ??
                    standing?.ranking_points ??
                    0,
                  eloPoints: standing?.rating_points ?? standing?.rating ?? 0,
                  totalPoints: standing?.total_points ?? 0,
                  player_profile: standing?.user_info?.player_profile || null,
                  participant_id: standing?.participant_id,
                  points: standing?.total_points ?? 0,
                  parents: rowParents,
                  country_flag: displayParent?.country_flag || '',
                  gender_id: standing?.gender_id ?? standing?.user_info?.gender_id ?? null,
                };
              })
              : [];
              
          const metadata =
          response?.data;
            
          this.totalPages = metadata?.metadata?.total_pages || 1;
          this.totalItems = metadata?.items[0]?.total_count || this.rankings.length || 0;
          this.currentPage = Math.min(
            Math.max(requestedPage, 1),
            this.totalPages,
          );
          this.itemsPerPage = metadata?.metadata?.per_page || this.itemsPerPage;

          if (this.currentPage === 1) {
            this.setPodiumTop3FromRankings();
          }

          if (rawData?.standings && Array.isArray(rawData.standings)) {
            this.updateEvents(rawData, this.searchTerm);
          }

          this.load_standings = false;
          this.hasLoadedStandings = true;
          this._showLoader = false;
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          this.load_standings = false;
          this.hasLoadedStandings = true;
          this.rankings = [];
          this.podiumTop3 = [];
          this.totalPages = 1;
          this.totalItems = 0;
          this.currentPage = 1;
          this._showLoader = false;
          console.error('Failed to fetch combined standings:', {
            status: error?.status,
            message: error?.message,
            error: error?.error,
          });
          this.cdr.detectChanges();
        },
        complete: () => {
          this.load_standings = false;
          this._showLoader = false;
          this.cdr.detectChanges();
        },
      });
  }

  private getCombinedSortType(): string {
    const allowedSortTypes = new Set([
      'ranking_points',
      'rating_points',
      'total_points',
      'rank',
    ]);
    return allowedSortTypes.has(this.sortBy) ? this.sortBy : 'rating_points';
  }

  systemSettings(): void {
    this.leaderboardService
      .getSystemSettings()
      .pipe(this.setupLoading.track(), takeUntilDestroyed(this.destroyRef))
      .subscribe((response: LeaderboardSystemSettings) => {
        this.leaderboardSystemSettings = response;
        this.leaderboardMode =
          this.leaderboardService.getCurrentLeaderboardMode();

        // If categories already loaded before settings resolved, trigger the correct first data load now.
        const levelId = this.levels.find((l) => l.level === this.selectedLevel)?.id;
        const categoryId = this.categories.find(
          (c) => c.category_description === this.selectedCategory,
        )?.id;
        if (
          levelId &&
          categoryId &&
          !this.hasTriggeredInitialStandingsLoad &&
          !this.load_standings
        ) {
          this.currentPage = 1;
          this.loadStandingsByMode(levelId, categoryId);
        }
      });
  }



  trackById(index: number, item: unknown): string | number {
    if (item != null && typeof item === 'object' && 'id' in item) {
      const id = (item as { id?: string | number }).id;
      if (id != null) return id;
    }
    return index;
  }
}
